import { css, type StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { TicketPriority, TicketStatus } from '../../../models/Support/SupportEnums';
import type { BulkTicketUpdate } from '../../../queries/Support/useSupportTicketMutations';
import { ChevronDown } from '../../../web/components/svgs/icons/ChevronDown';
import { Button } from '../../../web/components-v2/Button';
import { Dropdown, DropdownContent, DropdownItem, DropdownTrigger } from '../../../web/components-v2/Dropdown';
import { ConfirmModal } from '../../components/supportShared/ConfirmModal';
import { ticketTableStyles as ts } from './styles/ticketTableStyles';
import type { TicketFieldShortcut } from './useTicketShortcuts';

export interface EditMenuOption<T extends string = string> {
	value: T;
	label: string;
	/** Secondary value shown after the label, such as an agent's open-ticket count. */
	hint?: string;
	/** Hover text explaining the hint. */
	hintTitle?: string;
}

export const STATUS_EDIT_OPTIONS: ReadonlyArray<EditMenuOption<TicketStatus>> = [
	{ value: TicketStatus.Active, label: 'Active' },
	{ value: TicketStatus.Pending, label: 'Pending' },
	{ value: TicketStatus.Closed, label: 'Closed' },
	{ value: TicketStatus.Spam, label: 'Spam' },
	{ value: TicketStatus.Archive, label: 'Archive' },
];

export const PRIORITY_EDIT_OPTIONS: ReadonlyArray<EditMenuOption<TicketPriority>> = [
	{ value: TicketPriority.Low, label: 'Low' },
	{ value: TicketPriority.Normal, label: 'Normal' },
	{ value: TicketPriority.High, label: 'High' },
	{ value: TicketPriority.Urgent, label: 'Urgent' },
];

interface TicketEditMenuProps<T extends string> {
	children: React.ReactNode;
	options: ReadonlyArray<EditMenuOption<T>>;
	onSelect: (value: T) => void;
	triggerStyles: StyleDeclarationValue[];
	title?: string;
	disabled?: boolean;
	/** Controlled open state, for menus a keyboard shortcut can raise. Uncontrolled when omitted. */
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
}

export function TicketEditMenu<T extends string>({
	children,
	options,
	onSelect,
	triggerStyles,
	title,
	disabled,
	open,
	onOpenChange,
}: TicketEditMenuProps<T>) {
	return (
		<Dropdown open={open} onOpenChange={onOpenChange}>
			<DropdownTrigger
				disabled={disabled}
				render={<Button type='button' variant='ghost' styles={triggerStyles} title={title} />}
			>
				{children}
				<ChevronDown className={css(ts.cellEditChevron)} width={12} height={12} />
			</DropdownTrigger>
			<DropdownContent align='start' styles={[ts.filterMenu]}>
				{options.map(option => (
					<DropdownItem key={option.value} styles={[ts.filterMenuItem]} onClick={() => onSelect(option.value)}>
						{option.label}
						{option.hint !== undefined && (
							<span className={css(ts.menuItemHint)} title={option.hintTitle}>
								{option.hint}
							</span>
						)}
					</DropdownItem>
				))}
			</DropdownContent>
		</Dropdown>
	);
}

interface TicketBulkActionsBarProps {
	selectedCount: number;
	isPending: boolean;
	assigneeOptions: ReadonlyArray<EditMenuOption<string>>;
	categoryOptions: ReadonlyArray<EditMenuOption<string>>;
	/** Destination desks for a move. Omitted or empty hides the Move menu — there is nowhere to go. */
	mailboxOptions: ReadonlyArray<EditMenuOption<string>>;
	/** Which menu the `a` / `s` shortcuts have raised, if any; cleared through {@link onShortcutMenuChange}. */
	shortcutMenu?: TicketFieldShortcut | null;
	onShortcutMenuChange?: (menu: TicketFieldShortcut | null) => void;
	onApply: (update: BulkTicketUpdate) => void;
	onClear: () => void;
}

export const TicketBulkActionsBar: React.FC<TicketBulkActionsBarProps> = ({
	selectedCount,
	isPending,
	assigneeOptions,
	categoryOptions,
	mailboxOptions,
	shortcutMenu,
	onShortcutMenuChange,
	onApply,
	onClear,
}) => {
	const [pendingMailboxId, setPendingMailboxId] = React.useState<string | null>(null);
	const shortcutMenuProps = (menu: TicketFieldShortcut) =>
		onShortcutMenuChange
			? { open: shortcutMenu === menu, onOpenChange: (next: boolean) => onShortcutMenuChange(next ? menu : null) }
			: {};
	const pendingMailbox = mailboxOptions.find(option => option.value === pendingMailboxId);
	const ticketLabel = `${selectedCount} ticket${selectedCount === 1 ? '' : 's'}`;

	return (
		<>
			<div className={css(ts.selectionBar)}>
				<span className={css(ts.selectionCount)}>
					{isPending ? `Updating ${selectedCount}…` : `${selectedCount} selected`}
				</span>
				<TicketEditMenu
					options={assigneeOptions}
					onSelect={value => onApply({ field: 'assignee', assigneeId: value || null })}
					triggerStyles={[ts.bulkMenuBtn]}
					disabled={isPending}
					title='Assign the selected tickets (a)'
					{...shortcutMenuProps('assign')}
				>
					Assign
					<span className={css(ts.bulkMenuKey)} aria-hidden='true'>
						a
					</span>
				</TicketEditMenu>
				<TicketEditMenu
					options={STATUS_EDIT_OPTIONS}
					onSelect={status => onApply({ field: 'status', status })}
					triggerStyles={[ts.bulkMenuBtn]}
					disabled={isPending}
					title='Set the status of the selected tickets (s)'
					{...shortcutMenuProps('status')}
				>
					Status
					<span className={css(ts.bulkMenuKey)} aria-hidden='true'>
						s
					</span>
				</TicketEditMenu>
				<TicketEditMenu
					options={PRIORITY_EDIT_OPTIONS}
					onSelect={priority => onApply({ field: 'priority', priority })}
					triggerStyles={[ts.bulkMenuBtn]}
					disabled={isPending}
				>
					Priority
				</TicketEditMenu>
				<TicketEditMenu
					options={categoryOptions}
					onSelect={value => onApply({ field: 'category', categoryId: value || null })}
					triggerStyles={[ts.bulkMenuBtn]}
					disabled={isPending}
				>
					Category
				</TicketEditMenu>
				{mailboxOptions.length > 0 && (
					<TicketEditMenu
						options={mailboxOptions}
						onSelect={setPendingMailboxId}
						triggerStyles={[ts.bulkMenuBtn]}
						title='Move the selected tickets to another mailbox'
						disabled={isPending}
					>
						Move
					</TicketEditMenu>
				)}
				<Button type='button' variant='ghost' styles={[ts.selectionClearBtn]} onClick={onClear} disabled={isPending}>
					Clear selection
				</Button>
			</div>
			{pendingMailbox && (
				<ConfirmModal
					title={`Move to ${pendingMailbox.label}`}
					message={`Moving ${ticketLabel} to ${pendingMailbox.label}. The assignee and category are cleared on each, since both belong to the mailbox a ticket sits on, and each ticket takes a new number from ${pendingMailbox.label}'s sequence — the numbers they have now will no longer find them.`}
					confirmLabel={selectedCount === 1 ? 'Move ticket' : `Move ${selectedCount} tickets`}
					onConfirm={() => {
						const mailboxId = pendingMailbox.value;
						setPendingMailboxId(null);
						onApply({ field: 'mailbox', mailboxId });
					}}
					onCancel={() => setPendingMailboxId(null)}
				/>
			)}
		</>
	);
};
